import { useQuery } from '@apollo/client';
import { Button, Icon } from '@elipssolution/harfang';
import { mdiFileSign, mdiPlus } from '@mdi/js';
import { Alert, Stack } from '@mui/material';
import getConfig from 'next/config';
import { useRouter } from 'next/router';
import { useEffect, useMemo, useState } from 'react';

import ProcedureDialog from './ProcedureDialog';
import SignatureInvitationsKpis from './SignatureInvitationsKpis';
import PermissionWall from '../../../components/PermissionWall';
import { PublicRuntimeConfigType } from '../../../src/types/publicRuntimeConfig';
import { PermissionEnum } from '../../../types/permission';
import { FETCH_REMAINING_SIGNATURE_INVITATIONS, FetchRemainingSignatureInvitationsType } from '../api/signer';
import { ProcedureType } from '../types/procedure';

type ProcedureTableHeaderProps = {
	isProcedureDialogOpen?: boolean;
	procedureId?: ProcedureType['id'];
	onCloseProcedureDialog?: () => void;
};

const ProceduresTableHeader = ({
	isProcedureDialogOpen,
	procedureId,
	onCloseProcedureDialog,
}: ProcedureTableHeaderProps) => {
	const {
		publicRuntimeConfig: { yousignMassSignLink },
	} = getConfig() as PublicRuntimeConfigType;

	const { pathname } = useRouter();
	const isOnGoingSection = pathname.split('/').at(-1) === 'ongoing';

	const { data: remainingSignatureInvitationsData, loading: isRemainingSignatureInvitationsLoading } =
		useQuery<FetchRemainingSignatureInvitationsType>(FETCH_REMAINING_SIGNATURE_INVITATIONS, {});

	const remainingSignatures =
		remainingSignatureInvitationsData?.sign_remainingSignatureInvitations.signatureInvitations || 0;

	const [isOpenDialog, setIsOpenDialog] = useState(false);

	const handleCreateProcedure = () => setIsOpenDialog(true);

	const handleOpenMassSign = () => {
		window.open(yousignMassSignLink, 'blank');
	};

	const remainingSignaturesAlertText = useMemo(() => {
		if (remainingSignatures < 0) {
			return `Votre compteur d'invitations à signer est négatif, ce qui est anormal.
				Veuillez prendre contact avec votre administrateur.`;
		}

		return `Votre compteur d'invitations à signer est ${
			remainingSignatures > 0 ? 'bientôt ' : ''
		}épuisé. Vous pouvez faire une demande d'achat de
						pack d'invitations à signer via notre support.`;
	}, [remainingSignatures]);

	useEffect(() => {
		setIsOpenDialog(!!isProcedureDialogOpen);
	}, [isProcedureDialogOpen, setIsOpenDialog]);

	return (
		<>
			<Stack flexDirection="row" gap={2} alignItems="center">
				<PermissionWall permissionCodes={[PermissionEnum.SIGN_PROCEDURE_WRITE]}>
					<>
						<Button
							variant="contained"
							startIcon={<Icon path={mdiPlus} size="small" />}
							onClick={handleCreateProcedure}
						>
							Créer une procédure
						</Button>
						{isOnGoingSection && yousignMassSignLink && (
							<Button
								variant="contained"
								startIcon={<Icon path={mdiFileSign} size="small" />}
								onClick={handleOpenMassSign}
							>
								Signer en masse
							</Button>
						)}
					</>
				</PermissionWall>

				<SignatureInvitationsKpis
					remainingSignatureInvitations={remainingSignatures}
					isRemainingSignatureInvitationsLoading={isRemainingSignatureInvitationsLoading}
				/>

				<ProcedureDialog
					remainingSignatures={remainingSignatures}
					isOpen={isOpenDialog}
					procedureId={procedureId}
					onClose={() => {
						onCloseProcedureDialog?.();
						setIsOpenDialog(false);
					}}
				/>
			</Stack>

			{remainingSignatures < 20 && !isRemainingSignatureInvitationsLoading && (
				<Alert severity={remainingSignatures < 0 ? 'error' : 'warning'}>{remainingSignaturesAlertText}</Alert>
			)}
		</>
	);
};

export default ProceduresTableHeader;
